import { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

// Extended use case data
const useCases = [
// Chatbot category
{
  id: 1,
  title: "Customer Service AI Chatbot",
  description: "Automate customer inquiries and support on your website, 24/7.",
  category: "chatbot",
  icon: "💬"
},
{
  id: 2,
  title: "WhatsApp Business Assistant",
  description: "Engage with customers on WhatsApp with intelligent responses.",
  category: "chatbot",
  icon: "📱"
},
{
  id: 3,
  title: "E-commerce Shopping Assistant",
  description: "Help customers find products and answer questions about inventory.",
  category: "chatbot",
  icon: "🛍️"
},
{
  id: 4,
  title: "Appointment Booking Bot",
  description: "Let customers schedule appointments without human intervention.",
  category: "chatbot",
  icon: "📅"
},
{
  id: 5,
  title: "FAQ Chatbot",
  description: "Answer common questions instantly on your website or app.",
  category: "chatbot",
  icon: "❓"
},
// Voice category
{
  id: 6,
  title: "AI Voice Receptionist",
  description: "Handle incoming calls, route callers, and answer FAQs automatically.",
  category: "voice",
  icon: "📞"
},
{
  id: 7,
  title: "Lead Qualification Calls",
  description: "Qualify leads through automated voice calls and gather information.",
  category: "voice",
  icon: "🎯"
},
{
  id: 8,
  title: "Appointment Reminder Calls",
  description: "Reduce no-shows with automated reminder calls.",
  category: "voice",
  icon: "⏰"
},
{
  id: 9,
  title: "Customer Satisfaction Surveys",
  description: "Collect feedback through automated voice calls.",
  category: "voice",
  icon: "📊"
},
{
  id: 10,
  title: "Voice-Activated Order Status",
  description: "Let customers check order status through voice calls.",
  category: "voice",
  icon: "📦"
},
// Content category
{
  id: 11,
  title: "Social Media Content Generator",
  description: "Create engaging posts for multiple platforms from a single input.",
  category: "content",
  icon: "📱"
},
{
  id: 12,
  title: "Blog Post Generator",
  description: "Create SEO-optimized blog content from simple topic inputs.",
  category: "content",
  icon: "📝"
},
{
  id: 13,
  title: "Email Newsletter Automation",
  description: "Generate personalized newsletters based on user interests.",
  category: "content",
  icon: "✉️"
},
{
  id: 14,
  title: "Product Description Writer",
  description: "Create compelling product descriptions for e-commerce.",
  category: "content",
  icon: "🏷️"
},
{
  id: 15,
  title: "Video to Social Media Converter",
  description: "Convert YouTube videos to Instagram and TikTok posts.",
  category: "content",
  icon: "🎬"
},
// Workflow category
{
  id: 16,
  title: "Email Response Automation",
  description: "Auto-reply to common inquiries and categorize incoming emails.",
  category: "workflow",
  icon: "✉️"
},
{
  id: 17,
  title: "Product Recommendation Engine",
  description: "Suggest relevant products based on customer preferences and behavior.",
  category: "workflow",
  icon: "🛒"
},
{
  id: 18,
  title: "Resume Screening Automation",
  description: "Filter job applications based on qualifications and experience.",
  category: "workflow",
  icon: "📄"
},
{
  id: 19,
  title: "Data Entry Automation",
  description: "Extract information from documents and enter into systems.",
  category: "workflow",
  icon: "📊"
},
{
  id: 20,
  title: "Meeting Scheduler",
  description: "Coordinate schedules and set up meetings without back-and-forth emails.",
  category: "workflow",
  icon: "📅"
}];


export default function UseCasesPage() {
  const [category, setCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Set page title
    document.title = "AI Automation Use Cases | 50+ Ways to Automate Your Business";
  }, []);

  // Filter use cases based on category and search query
  const filteredUseCases = useCases.filter((useCase) => {
    const matchesCategory = category === "all" || useCase.category === category;
    const matchesSearch =
    useCase.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    useCase.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  return (
    <Layout data-id="cexfolhs5" data-path="src/pages/UseCasesPage.tsx">
      {/* Header section */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16" data-id="kzfwzm70y" data-path="src/pages/UseCasesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="v911i2co8" data-path="src/pages/UseCasesPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="dlzl2bb2r" data-path="src/pages/UseCasesPage.tsx">
            <h1 className="text-4xl font-bold text-gray-900 mb-6" data-id="ew16avlb5" data-path="src/pages/UseCasesPage.tsx">
              Browse Our AI Automation Use Cases
            </h1>
            <p className="text-lg text-gray-600 mb-8" data-id="ryjr29yad" data-path="src/pages/UseCasesPage.tsx">
              Discover Many Ways to Automate Your Business processes with AI technology.
              Filter by category or search to find the perfect solution for your needs.
            </p>
            
            {/* Search bar */}
            <div className="relative max-w-lg mx-auto" data-id="j9k585zlu" data-path="src/pages/UseCasesPage.tsx">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="iepinrsa9" data-path="src/pages/UseCasesPage.tsx">
                <Search className="h-5 w-5 text-gray-400" data-id="ssdqgoqot" data-path="src/pages/UseCasesPage.tsx" />
              </div>
              <Input
                type="text"
                placeholder="Search use cases..."
                className="light-input pl-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} data-id="1pjg08x2c" data-path="src/pages/UseCasesPage.tsx" />

            </div>
          </div>
        </div>
      </div>
      
      {/* Use cases section */}
      <div className="py-12 bg-white" data-id="0iehuofzc" data-path="src/pages/UseCasesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="jfwhz00z8" data-path="src/pages/UseCasesPage.tsx">
          <Tabs defaultValue="all" className="w-full mb-8" data-id="893b7he41" data-path="src/pages/UseCasesPage.tsx">
            <TabsList className="w-full max-w-2xl mx-auto grid grid-cols-2 sm:grid-cols-5 bg-gray-100" data-id="cuwkfaqgz" data-path="src/pages/UseCasesPage.tsx">
              <TabsTrigger
                value="all"
                onClick={() => setCategory("all")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="zpexg66b9" data-path="src/pages/UseCasesPage.tsx">

                All
              </TabsTrigger>
              <TabsTrigger
                value="chatbot"
                onClick={() => setCategory("chatbot")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="5r4som8ud" data-path="src/pages/UseCasesPage.tsx">

                Chatbots
              </TabsTrigger>
              <TabsTrigger
                value="voice"
                onClick={() => setCategory("voice")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="7exlrdonq" data-path="src/pages/UseCasesPage.tsx">

                Voice AI
              </TabsTrigger>
              <TabsTrigger
                value="content"
                onClick={() => setCategory("content")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="zft79izpj" data-path="src/pages/UseCasesPage.tsx">

                Content
              </TabsTrigger>
              <TabsTrigger
                value="workflow"
                onClick={() => setCategory("workflow")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="ndcqptmvx" data-path="src/pages/UseCasesPage.tsx">

                Workflow
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-8" data-id="6hye2idyr" data-path="src/pages/UseCasesPage.tsx">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="044o55xld" data-path="src/pages/UseCasesPage.tsx">
                {filteredUseCases.map((useCase) =>
                <Card key={useCase.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-id="lo5i42fqs" data-path="src/pages/UseCasesPage.tsx">
                    <CardContent className="p-6" data-id="22of2p9dg" data-path="src/pages/UseCasesPage.tsx">
                      <div className="text-4xl mb-4" data-id="npznjrrq5" data-path="src/pages/UseCasesPage.tsx">{useCase.icon}</div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="yrkqizx79" data-path="src/pages/UseCasesPage.tsx">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4" data-id="v30w7n8b5" data-path="src/pages/UseCasesPage.tsx">{useCase.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="chatbot" className="mt-8" data-id="ydy2wyxmd" data-path="src/pages/UseCasesPage.tsx">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="uztlmd1ct" data-path="src/pages/UseCasesPage.tsx">
                {filteredUseCases.map((useCase) =>
                <Card key={useCase.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-id="sqcbwmter" data-path="src/pages/UseCasesPage.tsx">
                    <CardContent className="p-6" data-id="vqonvtmhw" data-path="src/pages/UseCasesPage.tsx">
                      <div className="text-4xl mb-4" data-id="i8tjttj6l" data-path="src/pages/UseCasesPage.tsx">{useCase.icon}</div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="7z6d8s3bm" data-path="src/pages/UseCasesPage.tsx">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4" data-id="opqhrq0mo" data-path="src/pages/UseCasesPage.tsx">{useCase.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="voice" className="mt-8" data-id="lu1ssubbc" data-path="src/pages/UseCasesPage.tsx">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="h9e0rhu1s" data-path="src/pages/UseCasesPage.tsx">
                {filteredUseCases.map((useCase) =>
                <Card key={useCase.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-id="1q0ju2jay" data-path="src/pages/UseCasesPage.tsx">
                    <CardContent className="p-6" data-id="ymwyjw0xy" data-path="src/pages/UseCasesPage.tsx">
                      <div className="text-4xl mb-4" data-id="xqinb8qbf" data-path="src/pages/UseCasesPage.tsx">{useCase.icon}</div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="1t5q27qad" data-path="src/pages/UseCasesPage.tsx">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4" data-id="oqaaezalw" data-path="src/pages/UseCasesPage.tsx">{useCase.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="content" className="mt-8" data-id="8fw6c1d5y" data-path="src/pages/UseCasesPage.tsx">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="n4awuenrm" data-path="src/pages/UseCasesPage.tsx">
                {filteredUseCases.map((useCase) =>
                <Card key={useCase.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-id="40l0n9epq" data-path="src/pages/UseCasesPage.tsx">
                    <CardContent className="p-6" data-id="f6dhm6p6h" data-path="src/pages/UseCasesPage.tsx">
                      <div className="text-4xl mb-4" data-id="bxjo3w1f7" data-path="src/pages/UseCasesPage.tsx">{useCase.icon}</div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="3hbu0zzvc" data-path="src/pages/UseCasesPage.tsx">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4" data-id="dw3rnjakk" data-path="src/pages/UseCasesPage.tsx">{useCase.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="workflow" className="mt-8" data-id="xxmgzp53z" data-path="src/pages/UseCasesPage.tsx">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="x8pwz992j" data-path="src/pages/UseCasesPage.tsx">
                {filteredUseCases.map((useCase) =>
                <Card key={useCase.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-id="rlbfqag43" data-path="src/pages/UseCasesPage.tsx">
                    <CardContent className="p-6" data-id="zy0vvpw0y" data-path="src/pages/UseCasesPage.tsx">
                      <div className="text-4xl mb-4" data-id="o03sef0lr" data-path="src/pages/UseCasesPage.tsx">{useCase.icon}</div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="bwmsjo8e3" data-path="src/pages/UseCasesPage.tsx">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4" data-id="uhdukqw1j" data-path="src/pages/UseCasesPage.tsx">{useCase.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>);

}